<script setup lang="ts">
import { ref } from 'vue';

defineProps<{
  isPlaying: boolean;
}>();

defineEmits<{
  (e: 'toggle'): void;
}>();

const isHovered = ref(false);
</script>

<template>
  <div class="fixed top-6 right-6 z-[1000000] select-none">
    <button
      type="button"
      @click="$emit('toggle')"
      @mouseenter="isHovered = true"
      @mouseleave="isHovered = false"
      @focus="isHovered = true"
      @blur="isHovered = false"
      class="music-btn relative inline-flex items-center gap-3 px-4 py-2 border-0 bg-transparent cursor-pointer outline-none rounded-full"
      :aria-label="isPlaying ? 'Mute background music' : 'Play background music'"
    >
      <div class="fog-backdrop" aria-hidden="true"></div>

      <span class="relative z-10 flex items-end gap-[3px] h-3.5 w-4 pb-0.5" aria-hidden="true">
        <span class="eq-bar eq-bar-1" :class="{ 'is-paused': !isPlaying }"></span>
        <span class="eq-bar eq-bar-2" :class="{ 'is-paused': !isPlaying }"></span>
        <span class="eq-bar eq-bar-3" :class="{ 'is-paused': !isPlaying }"></span>
        <span class="eq-bar eq-bar-4" :class="{ 'is-paused': !isPlaying }"></span>
      </span>

      <span
        class="relative z-10 font-semibold uppercase tracking-[0.22em] text-[12px] text-[var(--color-dark,#491214)] whitespace-nowrap [text-shadow:_0_0_12px_rgba(255,255,255,0.9)]"
      >
        Music
      </span>

      <span
        class="relative z-10 text-[10px] font-bold uppercase tracking-[0.15em] transition-opacity duration-300"
        :class="isPlaying ? 'text-[var(--color-dark,#491214)] opacity-75' : 'text-[var(--color-dark,#491214)] opacity-40'"
      >
        {{ isPlaying ? 'on' : 'off' }}
      </span>
    </button>

    <Transition
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 translate-y-1.5 scale-95"
      leave-active-class="transition-all duration-200 ease-in"
      leave-to-class="opacity-0 translate-y-1 scale-95"
    >
      <div
        v-if="isHovered"
        class="absolute top-full right-0 mt-3 pointer-events-none w-72 sm:w-80 p-3 sm:p-3.5 rounded-[14px] border-2 border-[rgba(73,18,18,0.85)] bg-[rgba(255,255,255,0.94)] shadow-lg"
      >
        <p class="m-0 font-mono font-bold text-[12px] sm:text-[13px] leading-relaxed text-center text-[#491212]">
          Music is procedurally generated. It will change if you reload the page or stay longer.
        </p>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.music-btn {
  transition: transform 0.25s ease;
}

.music-btn:hover {
  transform: scale(1.04);
}

.music-btn:active {
  transform: scale(0.97);
}

.fog-backdrop {
  position: absolute;
  inset: -14px -28px;
  border-radius: 9999px;
  background:
    radial-gradient(
      ellipse 65% 55% at 50% 50%,
      rgba(255, 255, 255, 0.92) 0%,
      rgba(255, 255, 255, 0.8) 30%,
      rgba(255, 255, 255, 0.42) 55%,
      rgba(255, 255, 255, 0.1) 75%,
      rgba(255, 255, 255, 0) 90%
    ),
    radial-gradient(
      ellipse 85% 75% at 50% 50%,
      rgba(255, 255, 255, 0.38) 0%,
      rgba(255, 255, 255, 0.18) 45%,
      rgba(255, 255, 255, 0.05) 70%,
      rgba(255, 255, 255, 0.01) 88%,
      rgba(255, 255, 255, 0) 100%
    );
  pointer-events: none;
  transform: translateZ(0);
}

.eq-bar {
  width: 2px;
  height: 12px;
  background-color: var(--color-dark, #491214);
  border-radius: 9999px;
  transform-origin: bottom;
  transition: transform 0.35s ease, opacity 0.35s ease;
}

.eq-bar-1 { animation: eq-bounce-1 1.1s ease-in-out infinite alternate; }
.eq-bar-2 { animation: eq-bounce-2 0.75s ease-in-out infinite alternate; }
.eq-bar-3 { animation: eq-bounce-3 1.35s ease-in-out infinite alternate; }
.eq-bar-4 { animation: eq-bounce-4 0.9s ease-in-out infinite alternate; }

.eq-bar.is-paused {
  animation: none;
  transform: scaleY(0.2);
  opacity: 0.35;
}

@keyframes eq-bounce-1 {
  0% { transform: scaleY(0.25); }
  100% { transform: scaleY(1); }
}
@keyframes eq-bounce-2 {
  0% { transform: scaleY(0.85); }
  100% { transform: scaleY(0.2); }
}
@keyframes eq-bounce-3 {
  0% { transform: scaleY(0.15); }
  100% { transform: scaleY(0.95); }
}
@keyframes eq-bounce-4 {
  0% { transform: scaleY(0.7); }
  100% { transform: scaleY(0.3); }
}

@media (prefers-reduced-motion: reduce) {
  .eq-bar {
    animation: none;
  }
}
</style>
